import { Download, Loader2 } from "lucide-react";
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Banner } from "@/components/ui/banner";
import { useResources, RESOURCES_QUERY_KEY } from "@/hooks/queries/useResources";
import { useResourceDownload } from "@/hooks/queries/useResourceDownload";
import type { ResourceDocument } from "@/hooks/queries/resource-types";
import { toResourceDownloadError } from "@/routes/resources/resource-error";

// Resources tab (UX §13.7, Story 17.2) — lists the three CITS reference PDFs with a
// per-row Download CTA. Clinician-only: the route gate in app-routes.tsx denies
// org_admin/super_admin → /403, so this screen is reached only by clinicians (incl.
// legacy clinicians in onboarding, who hold role "clinician").
//
// Backend-truthful subset: Document title + Format token only — the 17.1 seed schema
// carries no byte-size column, so NO "MB" is rendered (never fabricate clinical-
// reference metadata). The Download action is wired to Story 17.3's
// GET /api/v1/resources/{id}/download-url: a click mints a FRESH short-TTL pre-signed
// S3 GET URL and the browser navigates to it same-tab (Content-Disposition:
// attachment makes it download in place — no new tab, no popup-blocker handling).
// NFR41: read-only for everyone who reaches it — no upload / replace / delete /
// rebrand affordance for any role. Tokens only; no pure-white background, no
// hardcoded hex.

// NFR41 copyright clause — must render VERBATIM (do not reword).
// The year is fixed legal text, not a calendar value: change it only when CITS
// revises their clause — never via new Date().getFullYear() or an annual rollover.
const COPYRIGHT_NOTICE =
  "© 2026 Center for Innovative Therapy Solutions (CITS). All rights reserved. These materials are provided for authorized AMPS training and use only. The content and branding are not configurable by your organization.";

const LOAD_ERROR_MESSAGE = "We couldn't load the reference materials. Please try again.";

export function ResourcesScreen() {
  const { data, isPending, isError, refetch } = useResources();
  const resources = data?.results ?? [];

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="display-xs text-[color:var(--text-primary)]">Reference materials</h1>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Downloadable reference materials supplied by CITS for clinical reference during AMPS
          assessments.
        </p>
      </header>

      {/* Error banner kept above the body so any prior data stays visible on a
          background-refetch failure (mirrors the clients screen). danger → role="alert". */}
      {isError ? (
        <Banner variant="danger" title="Couldn't load reference materials">
          <div className="flex flex-col gap-2">
            <span>{LOAD_ERROR_MESSAGE}</span>
            <span>
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            </span>
          </div>
        </Banner>
      ) : null}

      {/* Body: loading / empty / table. role="status" (polite live region) so AT
          announces the loading state; aria-busy marks the region as updating. */}
      {isPending ? (
        <div
          className="flex items-center justify-center rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-sm text-[color:var(--text-tertiary)]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          Loading reference materials…
        </div>
      ) : resources.length === 0 && !isError ? (
        <EmptyState />
      ) : resources.length > 0 ? (
        <ResourcesTable resources={resources} />
      ) : null}

      {/* NFR41: verbatim copyright clause below the table. info → role="status". */}
      <Banner variant="info">{COPYRIGHT_NOTICE}</Banner>
    </section>
  );
}

function EmptyState() {
  // Read-only screen: a benign, honest empty state — never "create one" (the corpus
  // is seeded by Story 17.1, so empty is not expected in production). No CTA.
  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-center">
      <p className="text-md text-[color:var(--text-secondary)]">
        No reference materials are available right now.
      </p>
    </div>
  );
}

function ResourcesTable({ resources }: { resources: ResourceDocument[] }) {
  return (
    <div className="overflow-x-auto rounded-none border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">CITS reference materials</caption>
        <thead>
          <tr className="border-b border-[color:var(--bg-quaternary)] text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
            <th scope="col" className="px-4 py-3 font-semibold">
              Document
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Format
            </th>
            <th scope="col" className="px-4 py-3 text-right font-semibold">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {resources.map((resource) => (
            <ResourceRow key={resource.id} resource={resource} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResourceRow({ resource }: { resource: ResourceDocument }) {
  const queryClient = useQueryClient();
  const download = useResourceDownload();
  // Inline, per-row error (mirrors the reveal-name pattern) — the app has no toast
  // layer, so a failed mint surfaces as a role="alert" line under THIS row's button.
  const [downloadError, setDownloadError] = React.useState<string | null>(null);

  const onDownload = () => {
    if (download.isPending) return; // ignore re-clicks while this row's URL is minting
    setDownloadError(null);
    download.mutate(resource.id, {
      onSuccess: (data) => {
        // The signed URL carries Content-Disposition: attachment, so a same-tab
        // navigation downloads in place and preserves the SPA — no new tab, no
        // popup-blocker handling. Each click re-mints, so the URL is always fresh.
        //
        // Defence in depth: the URL is server-minted and trusted (an S3 pre-signed
        // GET), but it flows straight into window.location.assign — so guard the
        // scheme before navigating. A non-https value (a contract drift, or a
        // javascript:/data: URI from a compromised response) is refused, never
        // navigated to.
        if (!data.download_url.startsWith("https://")) {
          setDownloadError("Couldn't start the download — please try again.");
          return;
        }
        window.location.assign(data.download_url);
      },
      onError: (error) => {
        const { message, shouldInvalidate } = toResourceDownloadError(error);
        setDownloadError(message);
        // RES-404 → the row is stale; refetch so it drops out of the table.
        if (shouldInvalidate) {
          void queryClient.invalidateQueries({ queryKey: [RESOURCES_QUERY_KEY] });
        }
      },
    });
  };

  return (
    <tr className="border-b border-[color:var(--bg-quaternary)] align-middle last:border-b-0">
      <td className="px-4 py-3 font-medium text-[color:var(--text-primary)]">{resource.title}</td>
      <td className="px-4 py-3 text-[color:var(--text-secondary)]">{resource.format}</td>
      <td className="px-4 py-3 text-right">
        {/* Wired to Story 17.3's pre-signed S3 GET. Enabled + keyboard-focusable; the
            accessible name is disambiguated by the document title so each row's control
            is distinct (AC8). Only THIS row's button shows a pending spinner while its
            URL is minting. */}
        <div className="flex flex-col items-end gap-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={onDownload}
            disabled={download.isPending}
            aria-label={`Download ${resource.title}`}
          >
            {download.isPending ? (
              <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
            ) : (
              <Download aria-hidden="true" className="h-4 w-4" />
            )}
            {download.isPending ? "Preparing…" : "Download"}
          </Button>
          {downloadError ? (
            <p role="alert" className="text-sm text-[color:var(--text-error)]">
              {downloadError}
            </p>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
